/**
 * Öffentliche Schnittstelle der Simulation.
 *
 * Alles, was von außen gebraucht wird, geht durch `Lauf`. Der innere
 * Weltzustand (`typen.ts`) ist absichtlich nicht Teil dieser Schnittstelle.
 */

import type { Zufall } from '@pg/rng';
import { signatur } from '@pg/rng';
import type { Absicht, Ereignis, Schnappschuss } from '@pg/protocol';
import { TAKTE_PRO_SEKUNDE, TAKT_DAUER } from '@pg/protocol';
import type { Inhalte } from '@pg/content';
import type { Welt } from './typen.js';
import { erstelleWelt, type WeltOptionen } from './welt.js';
import { schritt } from './schritt.js';
import { baueSchnappschuss, signaturWerte } from './schnappschuss.js';

export type { WeltOptionen } from './welt.js';
export type { Karte, Raum, Gegenstand, AffixInstanz, Automatikgrad } from './typen.js';
export { begehbar, zelle, FELS, BODEN, WAHRZEICHEN_BODEN } from './karte.js';
export { berechneWerte, erfahrungFuerStufe, skalierung } from './welt.js';
export {
  faelltEtwas,
  kategorie,
  basis,
  seltenheit,
  affixe,
  werte,
  praegelevel,
  baueName,
  wuerfleBeute,
} from './beute.js';
export {
  baueLagebild,
  regelGreift,
  waehleFaehigkeit,
  automatikErlaubt,
  sprungIstSicher,
} from './regeln.js';
export { systemNamen } from './schritt.js';
export { offenheit } from './karte.js';
export {
  erstelleWelt,
  baueAngebote,
  freischaltungAufStufe,
  standardFreischaltungen,
  hoechsterRang,
} from './welt.js';
// Messinstrument, kein Spielcode — siehe Kopfkommentar von testbot.ts.
export { botAbsichten, neuerBot, naechsterSchritt, STANDARD_BOT } from './testbot.js';
export type { BotEinstellung, BotZustand } from './testbot.js';
export { TAKTE_PRO_SEKUNDE, TAKT_DAUER };

/**
 * Ein laufender Abstieg.
 *
 * Der Aufrufer treibt die Simulation mit `takt()`. Wie oft das geschieht,
 * entscheidet er selbst — im Client aus einem Akkumulator gegen die echte Zeit,
 * im Balance-Runner so schnell wie möglich, im Test in fester Zahl.
 */
export class Lauf {
  readonly #welt: Welt;
  readonly #zufall: Zufall;
  readonly #inhalte: Inhalte;
  #ereignisse: Ereignis[] = [];

  constructor(optionen: WeltOptionen) {
    const { welt, zufall, inhalte } = erstelleWelt(optionen);
    this.#welt = welt;
    this.#zufall = zufall;
    this.#inhalte = inhalte;
  }

  /** Ein Simulationstakt. Gibt die Ereignisse dieses Takts zurück. */
  takt(absichten: readonly Absicht[] = []): Ereignis[] {
    const ergebnis = schritt(this.#welt, this.#zufall, this.#inhalte, absichten);
    this.#ereignisse = ergebnis.ereignisse;
    this.#letzteEffekte = ergebnis.effekte;
    return ergebnis.ereignisse;
  }

  #letzteEffekte: Schnappschuss['effekte'] = [];

  /** Sicht für den Renderer. Nur Daten, keine Verweise in den Weltzustand. */
  schnappschuss(): Schnappschuss {
    return baueSchnappschuss(this.#welt, this.#letzteEffekte);
  }

  ereignisse(): readonly Ereignis[] {
    return this.#ereignisse;
  }

  /** Karte für den Hintergrund. Ändert sich während eines Abstiegs nicht. */
  karte(): Welt['karte'] {
    return this.#welt.karte;
  }

  stand(): Welt['stand'] {
    return this.#welt.stand;
  }

  ertrag(): Welt['ertrag'] {
    return { ...this.#welt.ertrag };
  }

  pech(): Record<string, number> {
    return { ...this.#welt.pech };
  }

  takte(): number {
    return this.#welt.takt;
  }

  /** Signatur des aktuellen Zustands — Grundlage der goldenen Läufe. */
  signatur(): string {
    return signatur(signaturWerte(this.#welt));
  }

  /** Diagnose: wie oft wurde aus welchem Strom gezogen. */
  stromzaehler(): Record<string, number> {
    return this.#zufall.zaehler();
  }
}

/**
 * Führt einen Lauf headless über eine feste Zahl von Takten.
 * Grundlage für `tools/balance` und die goldenen Läufe.
 */
export function laufeHeadless(
  optionen: WeltOptionen,
  takte: number,
  absichtFuerTakt?: (takt: number) => readonly Absicht[],
): { lauf: Lauf; signatur: string; ereignisse: Ereignis[] } {
  const lauf = new Lauf(optionen);
  const alle: Ereignis[] = [];
  for (let i = 0; i < takte; i++) {
    const ereignisse = lauf.takt(absichtFuerTakt ? absichtFuerTakt(i) : []);
    for (const e of ereignisse) alle.push(e);
    if (lauf.stand() !== 'laeuft') break;
  }
  return { lauf, signatur: lauf.signatur(), ereignisse: alle };
}
